/**
 * JobList.jsx — Job list sliced into fixed, automatic buckets.
 *
 * Layout, top to bottom:
 *   [ search .................. ✕] [filters ▾] [+ New Job]
 *   (active filter chips, only when something is filtered)
 *   ▾ Needs attention       n · $x
 *   ▾ This week             n · $x
 *   ▾ New leads             n · $x     ← see lib/jobBuckets.js for the ladder
 *   ▸ Archived              n · $x     ← sub-grouped by month, collapsed
 *                                 n active · n archived
 *
 * Buckets are derived, never filed by hand: a job's status and dates decide
 * where it sits, so the list is right the moment a job changes. The old
 * drag-and-drop job_groups system this replaced sat empty on every profile.
 *
 * SEARCH FLATTENS. While the box has text, bucket headers are suppressed and
 * every match renders in one list — archived included. Buckets that are
 * collapsed must never be able to hide a search hit.
 */

import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Chip, Stack, MenuItem, Select,
  Divider, Popover, Tooltip, IconButton, Badge, InputAdornment, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TuneIcon from '@mui/icons-material/Tune';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import { fmtD, fmtC, STATUS_COLORS, formatStatus } from '../../lib/utils';
import { useJobs, useUpdateJob } from '../../hooks/useJobs';
import { useInvoices } from '../../hooks/useInvoices';
import { useToast } from '../layout/Toast';
import BulkInvoiceModal from '../invoices/BulkInvoiceModal';
import XeroReconcileDialog from './XeroReconcileDialog';
import { useXeroReconcile, useApplyReconcile } from '../../hooks/useXero';
import { useContacts } from '../../hooks/useContacts';
import { useRoster } from '../../hooks/useRoster';
import { usePinnedJobs } from '../../hooks/usePinnedJobs';
import { useMoneyExcludedJobs } from '../../hooks/useMoneyExcludedJobs';
import { bookedRevenue, jobDate } from '../../lib/jobMoney';
import { useRBAC } from '../../hooks/useRBAC';
import { normalizeSnapshot, deptLeads } from '../../lib/calcEngine';
import { bucketize, needsSplit, todayKey, BUCKETS } from '../../lib/jobBuckets';
import { tokenizeQuery, matchesTokens } from '../../lib/jobSearch';
import UserAvatar from '../shared/UserAvatar';
import FocusHide from '../shared/FocusHide';
import { getUserColor } from '../../lib/userColors';

// V1-style status priority: actionable statuses first
const STATUS_SORT_ORDER = ['shooting', 'post_production', 'confirmed', 'quoted', 'intake', 'partial_delivery', 'delivered', 'invoiced', 'paid', 'archived', 'void'];

// Statuses whose work is done and billable — the only ones offered for a
// bulk invoice. 'invoiced' is included for jobs stranded by a voided invoice.
const BULK_STATUSES = new Set(['delivered', 'partial_delivery', 'invoiced']);

const SORT_MODES = [
  { key: 'date', label: 'Newest' },
  { key: 'shoot', label: 'Shoot' },
  { key: 'status', label: 'Status' },
];

const LS_SORT_KEY = 'rfos_joblist_sort';
// Per-bucket collapse state, keyed by bucket key. Buckets that are history
// (paid, void, archived) start closed; see defaultCollapsed in jobBuckets.
const LS_BUCKET_COLLAPSE = 'rfos_joblist_bucket_collapsed';

/** Extract crew names from a calcSnapshot */
function getSnapshotCrew(snap) {
  const ns = normalizeSnapshot(snap);
  const names = new Map();
  ns.originators?.forEach((o) => { if (o.name) names.set(o.rosterId || o.name, o.name); });
  deptLeads(ns.prod).forEach((l) => { if (l.name) names.set(l.rosterId || l.name, l.name); });
  deptLeads(ns.post).forEach((l) => { if (l.name) names.set(l.rosterId || l.name, l.name); });
  return Array.from(names.values());
}

// Bucket header money uses the same predicate as the money strip (lib/jobMoney):
// confirmed-or-later, never voided. A bucket's count is every job in it, but
// its dollar figure is only the booked ones, so the two numbers can legitimately
// disagree — a bucket of 4 holding one quote shows "4 · $2.1k".
const sumRevenue = bookedRevenue;

/** Compact money for bucket headers, where fmtC's full form is too wide. */
function fmtCompact(n) {
  const abs = Math.abs(n);
  if (abs >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${Math.round(n)}`;
}

/** 'YYYY-MM' → 'Jul 2026' */
function monthLabel(key) {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

// Same date rule as the money strip (delivery unless it predates the shoot,
// else shoot, else created), so a job files under the same month in both.
function monthKeyOf(job) {
  const d = jobDate(job);
  return d ? d.slice(0, 7) : 'unknown';
}

// ── Job card ────────────────────────────────────────────────────────────────

function JobCard({ job, isSelected, onSelect, ctx }) {
  const { incompleteContactIds, roster, isPinned, togglePin, isExcluded, toggleExcluded, showMoneyToggle } = ctx;

  const isVoid = job.status === 'void';
  const statusColor = STATUS_COLORS[job.status] || '#666';
  const crew = getSnapshotCrew(job.calcSnapshot);
  const split = needsSplit(job);
  const typeBadge = job.jobType === 'Real Estate' ? 'RE' : job.jobType === 'Custom' ? 'CU' : null;
  const isNew = job.createdAt ? (Date.now() - new Date(job.createdAt).getTime()) < 24 * 60 * 60 * 1000 : false;
  const isIncomplete = job.contactId && incompleteContactIds.has(job.contactId);
  const isQuickAdd = job.source === 'quick_add' || isIncomplete;
  const pinned = isPinned(job.id);

  return (
    <Box
      onClick={() => onSelect(job)}
      sx={{
        px: 1.5, py: 1, cursor: 'pointer',
        borderLeft: 3,
        borderColor: isQuickAdd ? 'warning.main' : isSelected ? statusColor : 'transparent',
        bgcolor: isQuickAdd && !isSelected ? 'rgba(164,123,26,0.06)' : isSelected ? `${statusColor}12` : 'transparent',
        opacity: isVoid ? 0.55 : 1,
        touchAction: 'manipulation',
        '&:hover': { bgcolor: isSelected ? `${statusColor}18` : 'action.hover' },
        '& .pin-toggle': { opacity: pinned ? 1 : { xs: 0.35, md: 0 }, transition: 'opacity 0.15s' },
        '&:hover .pin-toggle': { opacity: 1 },
        '&:hover .bulk-toggle': { opacity: 1 },
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
        {ctx.bulkSelectable?.(job) && (() => {
          const selected = ctx.isBulkSelected(job.id);
          // Billable (invoice-eligible) jobs keep an always-visible checkbox —
          // it doubles as the "ready to bill" signal. Everything else reveals
          // on hover (or while a selection is active), like the pin toggle.
          const show = selected || ctx.anyBulkSelected || ctx.invoiceEligible(job);
          return (
            <Tooltip title="Select for bulk actions" arrow>
              <Box
                component="span"
                className="bulk-toggle"
                data-bulk-toggle={job.id}
                onClick={(e) => { e.stopPropagation(); ctx.toggleBulk(job); }}
                sx={{
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  p: { xs: 0.75, md: 0 }, m: { xs: -0.75, md: 0 },
                  color: selected ? 'primary.main' : 'text.disabled',
                  opacity: show ? 1 : { xs: 0.35, md: 0 },
                  transition: 'opacity 0.15s',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                {selected ? <CheckBoxIcon sx={{ fontSize: 16 }} /> : <CheckBoxOutlineBlankIcon sx={{ fontSize: 16 }} />}
              </Box>
            </Tooltip>
          );
        })()}
        <Chip
          label={formatStatus(job.status)}
          size="small"
          sx={{
            height: 18, fontSize: '0.6rem', fontFamily: '"DM Mono", monospace',
            fontWeight: 600, textTransform: 'uppercase',
            bgcolor: `${statusColor}20`, color: statusColor, border: `1px solid ${statusColor}40`,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        {typeBadge && (
          <Chip
            label={typeBadge}
            size="small"
            sx={{
              height: 18, fontSize: '0.55rem', fontFamily: '"DM Mono", monospace', fontWeight: 700,
              bgcolor: typeBadge === 'RE' ? 'rgba(26,127,163,0.09)' : 'rgba(123,94,167,0.09)',
              color: typeBadge === 'RE' ? 'info.main' : 'rfos.purple',
              '& .MuiChip-label': { px: 0.5 },
            }}
          />
        )}
        {split && (
          <Chip label="⚡" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(164,123,26,0.13)', color: 'warning.main', '& .MuiChip-label': { px: 0.4 } }} />
        )}
        {isQuickAdd && job.status === 'intake' && (
          <Chip
            label="TAKE ACTION"
            size="small"
            sx={{
              height: 18, fontSize: '0.55rem', fontFamily: '"DM Mono", monospace', fontWeight: 700,
              bgcolor: 'rgba(211,47,47,0.14)', color: 'error.main', border: '1px solid rgba(211,47,47,0.40)',
              '& .MuiChip-label': { px: 0.5 },
            }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        {showMoneyToggle && (
          <Tooltip title={isExcluded(job.id) ? 'Excluded from money totals' : 'Counted in money totals'} arrow>
            <Box
              component="span"
              onClick={(e) => { e.stopPropagation(); toggleExcluded(job.id); }}
              sx={{
                cursor: 'pointer', display: 'flex', alignItems: 'center',
                p: { xs: 0.75, md: 0 }, m: { xs: -0.75, md: 0 },
                color: isExcluded(job.id) ? 'warning.main' : 'text.disabled',
                '&:hover': { color: 'primary.main' },
              }}
            >
              {isExcluded(job.id) ? <CheckBoxOutlineBlankIcon sx={{ fontSize: 15 }} /> : <CheckBoxIcon sx={{ fontSize: 15 }} />}
            </Box>
          </Tooltip>
        )}
        <Box
          className="pin-toggle"
          component="span"
          onClick={(e) => { e.stopPropagation(); togglePin(job.id); }}
          sx={{
            cursor: 'pointer', display: 'flex', alignItems: 'center',
            p: { xs: 0.75, md: 0 }, m: { xs: -0.75, md: 0 },
            color: pinned ? 'primary.main' : 'text.disabled',
            '&:hover': { color: 'primary.main' },
          }}
        >
          {pinned ? <PushPinIcon sx={{ fontSize: 15 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 15 }} />}
        </Box>
        {isNew && (
          <Chip
            label="NEW"
            size="small"
            sx={{
              height: 22, fontSize: '0.7rem', fontFamily: '"DM Mono", monospace', fontWeight: 700,
              bgcolor: 'rgba(46,139,87,0.13)', color: 'success.main', border: '1px solid rgba(46,139,87,0.25)',
              '& .MuiChip-label': { px: 0.75 },
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.6 } },
            }}
          />
        )}
      </Stack>

      <Typography variant="body2" noWrap sx={{ fontWeight: 500, fontSize: '0.84rem', textDecoration: isVoid ? 'line-through' : 'none' }}>
        {job.title || '(Untitled)'}
      </Typography>

      <Typography variant="caption" color="text.secondary" noWrap sx={{ fontFamily: '"DM Mono", monospace', fontSize: '0.68rem' }}>
        {job.clientName || 'N/A'} · {fmtD(job.shootDate) || '—'}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.25 }}>
        {crew.length > 0 ? (
          <Stack direction="row" spacing={-0.5}>
            {crew.slice(0, 4).map((name) => {
              const rm = roster.find((r) => (r.name || '').includes(name) || name.includes(r.name || ''));
              return (
                <UserAvatar
                  key={name}
                  name={name}
                  color={getUserColor(rm || { name })}
                  size="sm"
                  style={{ border: '1.5px solid var(--surface, #fff)', marginLeft: crew.indexOf(name) > 0 ? -4 : 0 }}
                />
              );
            })}
            {crew.length > 4 && (
              <UserAvatar name={`+${crew.length - 4}`} color="#78716C" size="sm" style={{ border: '1.5px solid var(--surface, #fff)', marginLeft: -4 }} />
            )}
          </Stack>
        ) : (
          <Box />
        )}
        <Typography
          sx={{
            fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.1rem',
            color: isVoid ? 'text.disabled' : statusColor, lineHeight: 1,
            textDecoration: isVoid ? 'line-through' : 'none',
          }}
        >
          {fmtC(job.revenue)}
        </Typography>
      </Box>
    </Box>
  );
}

// ── Bucket header ───────────────────────────────────────────────────────────

function BucketHeader({ name, description, count, total, collapsed, onToggle, dim = false, tone, showMoney = false }) {
  return (
    <Tooltip title={description || ''} arrow placement="right" enterDelay={600}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          px: 1, py: 0.5, cursor: 'pointer',
          position: 'sticky', top: 0, zIndex: 2,
          bgcolor: 'background.paper',
          borderBottom: 1, borderColor: 'divider',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <ExpandMoreIcon
          sx={{ fontSize: 16, color: 'text.disabled', transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 150ms' }}
        />
        <Typography
          noWrap
          sx={{
            fontSize: '0.68rem', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
            color: tone === 'warning' ? 'warning.main' : dim ? 'text.disabled' : 'text.secondary',
            minWidth: 0,
          }}
        >
          {name}
        </Typography>
        <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled', fontFamily: '"DM Mono", monospace', flexShrink: 0 }}>
          {count}
        </Typography>
        {/* Aggregate money is company financials: payroll:full only. Per-job
            revenue on the cards is a separate, pre-existing exposure. */}
        {showMoney && count > 0 && (
          <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled', fontFamily: '"DM Mono", monospace', flexShrink: 0 }}>
            · {fmtCompact(total)}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

/** Job rows with a divider between them — the body of every bucket. */
function JobRows({ jobs, selectedJobId, onSelect, ctx }) {
  return jobs.map((job, i) => (
    <Box key={job.id}>
      <JobCard job={job} isSelected={selectedJobId === job.id} onSelect={onSelect} ctx={ctx} />
      {i < jobs.length - 1 && <Divider />}
    </Box>
  ));
}

// ── Main ────────────────────────────────────────────────────────────────────

// onQuickAdd is intentionally not consumed: the split "New Job" button was
// collapsed to a single button that opens the quote builder. Quick-add is still
// reachable via ?action=newJob from Contacts and both calendar views.
export default function JobList({ selectedJobId, onSelect }) {
  const { data: allJobs = [], isLoading, error } = useJobs();
  const { data: allInvoices = [] } = useInvoices();
  const { data: allContacts = [] } = useContacts();
  const { toast } = useToast();
  const { data: roster = [] } = useRoster();
  const { pinnedIds, isPinned, togglePin } = usePinnedJobs();
  const { isExcluded, toggleExcluded } = useMoneyExcludedJobs();
  const { hasAccess } = useRBAC();
  const showMoneyToggle = hasAccess('payroll', 'full');
  const navigate = useNavigate();

  // ── Xero reconcile ────────────────────────────────────────────────────────
  // Same gate as the server's xeroRead middleware (requireLevel invoices/create).
  const canReconcile = hasAccess('invoices', 'create');
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const reconcile = useXeroReconcile();
  const applyReconcile = useApplyReconcile();

  const runReconcile = () => {
    applyReconcile.reset();
    setReconcileOpen(true);
    reconcile.mutate(undefined, {
      // Without this a lapsed session 401s silently and the button reads as
      // broken — api.js only special-cases the role_changed 401.
      onError: (e) => toast(`Refresh failed: ${e.message}`, 'error'),
    });
  };

  const applyProposals = (transitions) => {
    applyReconcile.mutate(transitions, {
      onSuccess: (r) => {
        const n = r?.applied?.length || 0;
        toast(n ? `${n} job${n === 1 ? '' : 's'} updated` : 'No job statuses changed', n ? 'success' : 'info');
      },
      onError: (e) => toast(`Could not apply: ${e.message}`, 'error'),
    });
  };

  const incompleteContactIds = useMemo(() => {
    const s = new Set();
    allContacts.forEach((c) => { if (c.data?.incomplete) s.add(c.id); });
    return s;
  }, [allContacts]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [sortBy, setSortBy] = useState(() => {
    try { return localStorage.getItem(LS_SORT_KEY) || 'date'; } catch { return 'date'; }
  });
  const [filterEl, setFilterEl] = useState(null);
  const [openMonths, setOpenMonths] = useState({});
  const [collapsedBuckets, setCollapsedBuckets] = useState(() => {
    const defaults = Object.fromEntries(BUCKETS.filter((b) => b.defaultCollapsed).map((b) => [b.key, true]));
    try { return { ...defaults, ...JSON.parse(localStorage.getItem(LS_BUCKET_COLLAPSE) || '{}') }; } catch { return defaults; }
  });

  useEffect(() => {
    try { localStorage.setItem(LS_SORT_KEY, sortBy); } catch { /* noop */ }
  }, [sortBy]);
  useEffect(() => {
    try { localStorage.setItem(LS_BUCKET_COLLAPSE, JSON.stringify(collapsedBuckets)); } catch { /* noop */ }
  }, [collapsedBuckets]);

  const tokens = useMemo(() => tokenizeQuery(search), [search]);
  const searching = tokens.length > 0;

  // Filter + sort once; bucketing slices the result.
  const visible = useMemo(() => {
    let result = allJobs;
    if (statusFilter !== 'All') result = result.filter((j) => j.status === statusFilter.toLowerCase().replace(/ /g, '_'));
    if (typeFilter !== 'All') result = result.filter((j) => j.jobType === typeFilter);
    if (searching) result = result.filter((j) => matchesTokens(j, tokens));
    result = [...result];
    if (sortBy === 'date') result.sort((a, b) => new Date(b.shootDate || 0) - new Date(a.shootDate || 0));
    else if (sortBy === 'shoot') result.sort((a, b) => new Date(a.shootDate || '9999') - new Date(b.shootDate || '9999'));
    else if (sortBy === 'status') result.sort((a, b) => STATUS_SORT_ORDER.indexOf(a.status) - STATUS_SORT_ORDER.indexOf(b.status));
    return result;
  }, [allJobs, tokens, searching, statusFilter, typeFilter, sortBy]);

  // Recomputed per render batch rather than per job, so every bucket in one
  // pass agrees on what "today" is even across a midnight boundary.
  const today = todayKey();
  const buckets = useMemo(
    () => (searching ? [] : bucketize(visible, { today, isPinned })),
    [visible, searching, today, isPinned],
  );

  const archivedByMonth = useMemo(() => {
    const archived = buckets.find((b) => b.key === 'archived')?.jobs || [];
    const m = new Map();
    for (const j of archived) {
      const k = monthKeyOf(j);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(j);
    }
    return [...m.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [buckets]);

  const jobTypes = useMemo(() => {
    const types = new Set(allJobs.map((j) => j.jobType).filter(Boolean));
    return ['All', ...Array.from(types).sort()];
  }, [allJobs]);

  const activeCount = useMemo(
    () => allJobs.filter((j) => j.status !== 'archived' && j.status !== 'void').length,
    [allJobs],
  );
  const archivedCount = useMemo(() => allJobs.filter((j) => j.status === 'archived').length, [allJobs]);

  const activeFilterCount = (statusFilter !== 'All' ? 1 : 0) + (typeFilter !== 'All' ? 1 : 0) + (sortBy !== 'date' ? 1 : 0);

  // ── Bulk invoice selection ────────────────────────────────────────────────
  // A job is offered for bulk invoicing when its work is done (BULK_STATUSES)
  // and no active balance/full invoice already covers it — directly via
  // job_id or through a bulk invoice's job_ids. Paid deposits don't block;
  // the modal bills revenue minus the paid deposit.
  const coveredJobIds = useMemo(() => {
    const s = new Set();
    allInvoices.forEach((inv) => {
      if (inv.status === 'void') return;
      const t = inv.invoiceType || 'full';
      if (t !== 'balance' && t !== 'full') return;
      if (inv.jobId) s.add(inv.jobId);
      if (Array.isArray(inv.jobIds)) inv.jobIds.forEach((id) => s.add(id));
    });
    return s;
  }, [allInvoices]);

  const [bulkIds, setBulkIds] = useState([]);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);

  const bulkJobs = useMemo(
    () => bulkIds.map((id) => allJobs.find((j) => j.id === id)).filter(Boolean),
    [bulkIds, allJobs],
  );
  const bulkTotal = useMemo(
    () => bulkJobs.reduce((s, j) => s + (parseFloat(j.revenue) || 0), 0),
    [bulkJobs],
  );
  // Any active job can be selected for bulk STATUS changes (mostly archiving).
  // Archived is terminal and void only walks back to intake — neither belongs
  // in a bulk sweep.
  const bulkSelectable = (job) => !['archived', 'void'].includes(job.status);
  // Invoice-eligible is stricter: work done, a real client name (blank names
  // would let unrelated clients bundle), and not already covered.
  const invoiceEligible = (job) => BULK_STATUSES.has(job.status) && !!job.clientName && !coveredJobIds.has(job.id);
  // The invoice action gates itself: every selected job billable + one client.
  const invoiceReady = bulkJobs.length > 0
    && bulkJobs.every(invoiceEligible)
    && new Set(bulkJobs.map((j) => j.clientName)).size === 1;
  const isBulkSelected = (id) => bulkIds.includes(id);
  const toggleBulk = (job) => setBulkIds((ids) => (
    ids.includes(job.id) ? ids.filter((i) => i !== job.id) : [...ids, job.id]
  ));

  const updateJobMutation = useUpdateJob();
  const applyBulkStatus = async (status) => {
    if (!bulkJobs.length) return;
    const label = formatStatus(status);
    if (!window.confirm(`Set ${bulkJobs.length} job${bulkJobs.length === 1 ? '' : 's'} to ${label}?`)) return;
    let ok = 0;
    const failed = [];
    for (const j of bulkJobs) {
      try {
        await updateJobMutation.mutateAsync({ id: j.id, status });
        ok++;
      } catch (err) {
        failed.push(j.title || j.id);
      }
    }
    if (failed.length) {
      toast(`${ok} set to ${label} — ${failed.length} rejected (invalid transition): ${failed.slice(0, 3).join(', ')}${failed.length > 3 ? '…' : ''}`, 'warning');
    } else {
      toast(`${ok} job${ok === 1 ? '' : 's'} set to ${label}`);
    }
    setBulkIds([]);
  };

  const ctx = {
    incompleteContactIds, roster, isPinned, togglePin, isExcluded, toggleExcluded, showMoneyToggle,
    bulkSelectable, invoiceEligible, isBulkSelected, toggleBulk, anyBulkSelected: bulkIds.length > 0,
  };

  const toggleBucket = (key) => setCollapsedBuckets((c) => ({ ...c, [key]: !c[key] }));

  if (error) return <Paper sx={{ p: 2 }}><Typography color="error" variant="body2">Failed to load jobs: {error.message}</Typography></Paper>;

  const statuses = ['All', 'Intake', 'Quoted', 'Confirmed', 'Shooting', 'Post Production', 'Partial Delivery', 'Delivered', 'Invoiced', 'Paid', 'Void'];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
      {/* One-row toolbar: search + filters + new job */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <TextField
          size="small"
          placeholder="Search jobs…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') setSearch(''); }}
          sx={{ flex: 1, minWidth: 0 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 17, color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.25 }} aria-label="Clear search">
                  <CloseIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Tooltip title="Filters and sort" arrow>
          <IconButton size="small" onClick={(e) => setFilterEl(e.currentTarget)} sx={{ flexShrink: 0 }}>
            <Badge badgeContent={activeFilterCount} color="primary" overlap="circular">
              <TuneIcon sx={{ fontSize: 19 }} />
            </Badge>
          </IconButton>
        </Tooltip>
        {canReconcile && (
          <FocusHide k="jobs.xeroReconcile">
            <Tooltip title="Refresh payment status from Xero and re-check job statuses" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={runReconcile}
                  disabled={reconcile.isPending}
                  sx={{ flexShrink: 0 }}
                  aria-label="Refresh payment status from Xero"
                >
                  {reconcile.isPending
                    ? <CircularProgress size={17} />
                    : <CloudSyncOutlinedIcon sx={{ fontSize: 19 }} />}
                </IconButton>
              </span>
            </Tooltip>
          </FocusHide>
        )}
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => navigate('/quotes')}
          sx={{ textTransform: 'none', fontSize: '0.75rem', flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          New Job
        </Button>
      </Box>

      <Popover
        open={!!filterEl}
        anchorEl={filterEl}
        onClose={() => setFilterEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Stack spacing={1.25} sx={{ p: 1.5, minWidth: 210 }}>
          <Select size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ fontSize: '0.75rem' }}>
            {statuses.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: '0.75rem' }}>{s === 'All' ? 'All statuses' : s}</MenuItem>)}
          </Select>
          {jobTypes.length > 2 && (
            <Select size="small" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} sx={{ fontSize: '0.75rem' }}>
              {jobTypes.map((t) => <MenuItem key={t} value={t} sx={{ fontSize: '0.75rem' }}>{t === 'All' ? 'All types' : t}</MenuItem>)}
            </Select>
          )}
          <Select size="small" value={sortBy} onChange={(e) => setSortBy(e.target.value)} sx={{ fontSize: '0.75rem' }} renderValue={(v) => `Sort: ${SORT_MODES.find((m) => m.key === v)?.label ?? v}`}>
            {SORT_MODES.map((m) => <MenuItem key={m.key} value={m.key} sx={{ fontSize: '0.75rem' }}>{m.label}</MenuItem>)}
          </Select>
        </Stack>
      </Popover>

      {/* Active filters, shown only when set — clean state costs no vertical space */}
      {activeFilterCount > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {statusFilter !== 'All' && <Chip size="small" label={statusFilter} onDelete={() => setStatusFilter('All')} sx={{ height: 20, fontSize: '0.65rem' }} />}
          {typeFilter !== 'All' && <Chip size="small" label={typeFilter} onDelete={() => setTypeFilter('All')} sx={{ height: 20, fontSize: '0.65rem' }} />}
          {sortBy !== 'date' && <Chip size="small" label={`Sort: ${SORT_MODES.find((m) => m.key === sortBy)?.label}`} onDelete={() => setSortBy('date')} sx={{ height: 20, fontSize: '0.65rem' }} />}
        </Stack>
      )}

      <Paper sx={{ flex: 1, overflowY: 'auto', position: 'relative' }}>
        {isLoading ? (
          <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block', textAlign: 'center' }}>Loading jobs...</Typography>
        ) : visible.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {searching ? `No jobs match “${search.trim()}”` : 'No jobs found'}
            </Typography>
            {searching && (
              <Button size="small" onClick={() => setSearch('')} sx={{ textTransform: 'none', fontSize: '0.7rem', mt: 0.5 }}>
                Clear search
              </Button>
            )}
          </Box>
        ) : searching ? (
          /* Search flattens: no bucket headers, nothing collapsed, archived
             included — a hit can never be hiding behind a closed header. */
          <>
            <Box sx={{ px: 1, py: 0.5, position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
              <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled', fontFamily: '"DM Mono", monospace' }}>
                {visible.length} {visible.length === 1 ? 'result' : 'results'}
                {showMoneyToggle ? ` · ${fmtCompact(sumRevenue(visible))}` : ''}
              </Typography>
            </Box>
            <JobRows jobs={visible} selectedJobId={selectedJobId} onSelect={onSelect} ctx={ctx} />
          </>
        ) : (
          buckets.map((b) => {
            const collapsed = !!collapsedBuckets[b.key];
            return (
              <Box key={b.key}>
                <BucketHeader
                  name={b.label}
                  description={b.description}
                  count={b.jobs.length}
                  total={sumRevenue(b.jobs)}
                  showMoney={showMoneyToggle}
                  collapsed={collapsed}
                  onToggle={() => toggleBucket(b.key)}
                  dim={b.dim}
                  tone={b.tone}
                />
                {!collapsed && (b.months ? (
                  /* Archived is a wall of 60+ jobs — nest it by month. */
                  archivedByMonth.map(([key, list]) => {
                    const open = !!openMonths[key];
                    return (
                      <Box key={key}>
                        <Box
                          onClick={() => setOpenMonths((m) => ({ ...m, [key]: !open }))}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5, pl: 3, pr: 1, py: 0.4,
                            cursor: 'pointer', bgcolor: 'action.hover',
                            '&:hover': { bgcolor: 'action.selected' },
                          }}
                        >
                          <ExpandMoreIcon sx={{ fontSize: 14, color: 'text.disabled', transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform 150ms' }} />
                          <Typography sx={{ fontSize: '0.64rem', color: 'text.secondary', fontFamily: '"DM Mono", monospace' }}>
                            {key === 'unknown' ? 'No date' : monthLabel(key)}
                          </Typography>
                          <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', fontFamily: '"DM Mono", monospace', ml: 'auto' }}>
                            {list.length}{showMoneyToggle ? ` · ${fmtCompact(sumRevenue(list))}` : ''}
                          </Typography>
                        </Box>
                        {open && <JobRows jobs={list} selectedJobId={selectedJobId} onSelect={onSelect} ctx={ctx} />}
                      </Box>
                    );
                  })
                ) : (
                  <JobRows jobs={b.jobs} selectedJobId={selectedJobId} onSelect={onSelect} ctx={ctx} />
                ))}
              </Box>
            );
          })
        )}
      </Paper>

      {/* Bulk action bar — appears while jobs are selected */}
      {bulkJobs.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, px: 1.25, py: 0.75,
            border: 1, borderColor: 'primary.main',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
            {bulkJobs.length} selected · {fmtC(bulkTotal)}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={() => setBulkIds([])} sx={{ textTransform: 'none', color: 'text.secondary', minWidth: 0 }}>
            Clear
          </Button>
          <Select
            size="small"
            value=""
            displayEmpty
            renderValue={() => 'Status…'}
            onChange={(e) => { if (e.target.value) applyBulkStatus(e.target.value); }}
            sx={{ fontSize: '0.72rem', '& .MuiSelect-select': { py: 0.5 } }}
          >
            {['invoiced', 'paid', 'archived', 'void'].map((s) => (
              <MenuItem key={s} value={s} sx={{ fontSize: '0.75rem' }}>{formatStatus(s)}</MenuItem>
            ))}
          </Select>
          <Button
            size="small"
            variant="outlined"
            onClick={() => applyBulkStatus('archived')}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Archive
          </Button>
          <Tooltip
            title={invoiceReady ? '' : 'One invoice needs billable jobs (delivered, not yet invoiced) from a single client'}
            arrow
          >
            <span>
              <Button
                size="small"
                variant="contained"
                disabled={!invoiceReady}
                startIcon={<ReceiptLongOutlinedIcon sx={{ fontSize: 16 }} />}
                onClick={() => setBulkModalOpen(true)}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                One invoice
              </Button>
            </span>
          </Tooltip>
        </Paper>
      )}

      <XeroReconcileDialog
        open={reconcileOpen}
        onClose={() => setReconcileOpen(false)}
        result={reconcile.data}
        loading={reconcile.isPending}
        error={reconcile.error}
        onApply={applyProposals}
        applying={applyReconcile.isPending}
        applyResult={applyReconcile.data}
      />

      <BulkInvoiceModal
        open={bulkModalOpen}
        jobs={bulkJobs}
        onClose={() => setBulkModalOpen(false)}
        onCreated={(inv) => {
          setBulkModalOpen(false);
          setBulkIds([]);
          navigate(`/invoices?select=${inv.id}`);
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto', fontSize: '0.68rem' }}>
          {activeCount} active{archivedCount > 0 ? ` · ${archivedCount} archived` : ''}
        </Typography>
      </Box>
    </Box>
  );
}
